import { useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import Editor from "../components/blog-editor/Editor";
import toast from "react-hot-toast";
import axios from "../utils/axios";
import { getBlogCategories } from "../api/blogCategories";

const BlogsDetails = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [blogData, setBlogData] = useState(null);
  const [blogStatus, setBlogStatus] = useState(null);
  const [isInCarousel, setIsInCarousel] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [categoryIds, setCategoryIds] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Handle save with API call
  const handleSave = useCallback(
    async (content) => {
      try {
        setIsSaving(true);
        const normalizedCountryCodes = (countryCodes || []).map((c) =>
          String(c).toUpperCase()
        );
        await axios.put(`/admin/blogs/data/${id}`, {
          blogData: content,
          status: blogStatus,
          isInCarousel: isInCarousel === "true" ? true : false,
          countryCodes: normalizedCountryCodes,
          categoryIds: categoryIds || [],
        });

        toast.success("Content saved successfully!");
      } catch (error) {
        console.error("Error saving content:", error);
        toast.error("Failed to save content");
      } finally {
        setIsSaving(false);
      }
    },
    [id, blogStatus, isInCarousel, countryCodes, categoryIds]
  );
  console.log("isInCarousel", isInCarousel);
  // Handle load with API call
  const handleLoad = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("Loading content...");

      // Fetch blog and categories in parallel
      const [blogResponse, categoriesResponse] = await Promise.all([
        axios.get(`/admin/blogs/${id}`),
        getBlogCategories(),
      ]);

      const blogInfo = blogResponse?.data?.data || blogResponse.data;
      setAllCategories(categoriesResponse?.data || []);
      const blogContent = blogInfo?.blogData || blogInfo?.content || null;

      // Set blog information
      setBlogData(blogInfo);
      setBlogStatus(blogInfo?.status || "DRAFT");
      setIsInCarousel(blogInfo?.isInCarousel || false);
      setCountryCodes(
        Array.isArray(blogInfo?.countryCodes)
          ? blogInfo.countryCodes.map((c) => String(c).toUpperCase())
          : []
      );
      setCategoryIds(
        Array.isArray(blogInfo?.categories)
          ? blogInfo.categories.map((c) => c._id)
          : []
      );

      // Set editor data - use API data if exists, otherwise null for default
      if (blogContent && blogContent.blocks && blogContent.blocks.length > 0) {
        setData(blogContent);
        console.log("Loaded blog content from API:", blogContent);
      } else {
        setData(null); // This will trigger default data in Editor
        console.log("No blog content found, using default");
      }

      toast.success("Content loaded successfully!");
      return blogContent;
    } catch (error) {
      console.error("Error loading content:", error);
      toast.error("Failed to load content");
      // Set default data on error
      setData(null);
      setBlogData(null);
      setBlogStatus("DRAFT");
      setCategoryIds([]);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Handle clear with external logic
  const handleClear = useCallback(() => {
    console.log("Content cleared");
    toast.success("Content cleared");
  }, []);

  // Handle export with external logic
  const handleExport = useCallback((content) => {
    try {
      console.log("Exporting content:", content);

      // Create a more user-friendly export
      const exportData = {
        title: "Blog Content Export",
        timestamp: new Date().toISOString(),
        content: content,
        wordCount:
          content.blocks?.reduce((count, block) => {
            if (block.type === "paragraph" || block.type === "header") {
              return count + (block.data.text || "").split(" ").length;
            }
            return count;
          }, 0) || 0,
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `blog-content-${
        new Date().toISOString().split("T")[0]
      }.json`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success("Content exported successfully!");
    } catch (error) {
      console.error("Error exporting content:", error);
      toast.error("Failed to export content");
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    if (id) {
      handleLoad();
    }
  }, [id, handleLoad]);

  useEffect(() => {
    console.log("Data:", data);
  }, [data]);

  return (
    <div className="w-full min-h-screen">
      <div className="w-full max-w-none">
        {/* <div className="mb-6 px-6 relative">
          <ArrowLeftIcon
            className="h-6 w-6 cursor-pointer absolute top-0 left-0"
            onClick={() => navigate("/dashboard/blogs")}
          />{" "}
          <div className="flex flex-col items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              {blogData?.title}
            </h1>
            <p className="text-gray-600">
              {!blogData?.description
                ? "No description"
                : blogData?.description}
            </p>
          </div>
        </div> */}

        {isLoading ? (
          <div className="w-full px-0">
            <div className="flex items-center justify-center h-64">
              <div className="inline-flex items-center gap-2 text-gray-600">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                Loading blog content...
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full px-0">
            <Editor
              data={
                data || {
                  time: Date.now(),
                  blocks: [
                    {
                      id: "SzwhuyoFq6",
                      type: "paragraph",
                      data: {
                        text: "Enter your blog content here...",
                      },
                    },
                  ],
                }
              }
              setData={setData}
              onSave={handleSave}
              onLoad={handleLoad}
              onClear={handleClear}
              onExport={handleExport}
              showControls={true}
              readOnly={false}
              blogStatus={blogStatus}
              setBlogStatus={setBlogStatus}
              setIsInCarousel={setIsInCarousel}
              isInCarousel={isInCarousel}
              countryCodes={countryCodes}
              setCountryCodes={setCountryCodes}
              categoryIds={categoryIds}
              setCategoryIds={setCategoryIds}
              allCategories={allCategories}
              allData={blogData}
            />
          </div>
        )}

        {isSaving && (
          <div className="mt-4 text-center px-6">
            <div className="inline-flex items-center gap-2 text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              Saving content...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogsDetails;
